/**
 * Shopify + Cloudlift Bundle Handler
 * -------------------------------------
 * This script detects when Cloudlift's options have rendered,
 * hijacks the add-to-cart form submission for bundle products,
 * dynamically builds the line item properties, and sends a single
 * POST request to /cart/add.js with multiple variants.
 *
 * Follow this reference for the Shopify cart API:
 * https://shopify.dev/docs/api/ajax/reference/cart
 *
 * Disables the button during request
 * Prevents Shopify default handler from running
 * Compatible with dynamic DOM injection via MutationObserver
 *
 * Bundle item design types (controlled by the suffix on _option_bundle_product_id_x in CLPO):
 *
 *   _design    => standard design/color options
 *                  properties[design] + any properties[color_*] without a numbered suffix
 *
 *   _design_1  => tech-label design/color options (settings renamed from *_tech_label)
 *                  properties[design_design_1] + any properties[color_*_design_1]
 *
 *   _design_2  => shoe-label / large duffel design/color options (settings renamed from *_copy)
 *                  properties[design_design_2] + any properties[color_*_design_2]
 *
 * Multiple bundle items of the same type are all supported.
 * Items without a design suffix receive only the global line properties.
 *
 * Author: MH Media
 * Date: 2025/05/13
 * Updated: 2026/06/23
 */

document.addEventListener('DOMContentLoaded', function () {
  window.addEventListener('options:rendered', function () {
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    const addToCartBtn = productForm?.querySelector('[type="submit"]');
    const optionsWrapper = document.querySelector('.cl-po--options');

    if (!productForm || !addToCartBtn || !optionsWrapper) return;

    productForm.addEventListener(
      'submit',
      function (e) {
        const isBundle = optionsWrapper.querySelector(
          '[name^="properties[_option_bundle_product_id_"]'
        );
        if (!isBundle) return;

        e.preventDefault();
        e.stopImmediatePropagation(); // Prevent Shopify's native form submission

        const variantIds = getBundleVariantIds(optionsWrapper);
        if (!variantIds.length) {
          console.warn('No variant IDs found.');
          return;
        }

        const properties = getLineProperties(optionsWrapper);
        const formData = buildCartFormData(variantIds, properties, optionsWrapper);

        hideFormError(productForm);
        disableAddToCartBtn(addToCartBtn, 'Adding...');

        let hadError = false;

        sendToCart(formData)
          .then((result) => {
            if (window.cartDrawer) {
              window.cartDrawer.renderContents(result);
            } else if (window.cartNotification) {
              window.cartNotification.renderContents(result);
            }
          })
          .catch((error) => {
            console.error('Error adding bundle:', error);
            hadError = true;
            showFormError(productForm, 'There was an error adding your bundle. Please try again.');
          })
          .finally(() => {
            enableAddToCartBtn(addToCartBtn, 'Add to Cart');
            if (hadError) addToCartBtn.focus();
          });
      },
      { capture: true }
    );
  });
});

/**
 * Dynamically extracts all variant IDs from Cloudlift hidden inputs.
 * @param {Element} wrapper - The DOM element containing the options.
 * @returns {string[]} Array of variant ID strings.
 */
function getBundleVariantIds(wrapper) {
  const inputs = wrapper.querySelectorAll('[name^="properties[_option_bundle_product_id_"]');
  return Array.from(inputs)
    .map((input) => input.value)
    .filter(Boolean);
}

// All supported design-type suffixes used as data-option endings in CLPO.
const DESIGN_TYPES = ['_design', '_design_1', '_design_2'];

/**
 * Extracts line item properties from the Cloudlift options UI.
 *
 * - Collects global properties from all `option_line_*` inputs (applied to every bundle item).
 * - For each design type present in the bundle (_design, _design_1, _design_2), reads the
 *   corresponding design/color selections from the appropriate CLPO property inputs.
 *
 * @param {Element} wrapper - The DOM element containing the Cloudlift options UI.
 * @returns {{ globalProps: Object, designPropsMap: Object }}
 *   - globalProps:    shared by all bundle line items
 *   - designPropsMap: keyed by design type suffix, each holding Design/Color props for that type
 */
function getLineProperties(wrapper) {
  const lineInputs = wrapper.querySelectorAll('[name^="properties[option_line_"]');
  const globalProps = {};

  // Line inputs go to all items
  lineInputs.forEach((input) => {
    const nameAttr = input.getAttribute('name');
    const keyMatch = nameAttr.match(/\[([^\]]+)\]/);
    if (keyMatch && input.value) {
      const label = keyMatch[1].replace('option_line_', 'Line ').replace(/_/g, ' ').trim();
      globalProps[label] = input.value;
    }
  });

  // Build design props for each type that exists in this bundle
  const designPropsMap = {};

  for (const type of DESIGN_TYPES) {
    if (!wrapper.querySelector(`[data-option$="${type}"]`)) continue;
    designPropsMap[type] = resolveDesignProps(wrapper, type);
  }

  return { globalProps, designPropsMap };
}

/**
 * Resolves the Design and Color properties for a given design type.
 *
 * For _design: reads properties[design] and whichever color_* input is checked,
 * excluding inputs belonging to _design_1 / _design_2 (those end with _design_N]).
 * This handles all conditional color variants (color_solid, Color_geo, Color_Stripes,
 * etc.) without needing to enumerate them explicitly.
 *
 * For _design_1 / _design_2: reads properties[design{type}] and whichever color_*
 * input ends with the type suffix (e.g. color_solid_design_1]).
 *
 * @param {Element} wrapper
 * @param {string} type - '_design' | '_design_1' | '_design_2'
 * @returns {Object}
 */
function resolveDesignProps(wrapper, type) {
  const props = {};

  if (type === '_design') {
    const selectedDesign = wrapper.querySelector('input[name="properties[design]"]:checked');
    // Pick whichever standard color input is checked; exclude suffixed ones (_design_1, _design_2, etc.)
    const allColorInputs = wrapper.querySelectorAll(
      'input[name^="properties[color_"]:checked, input[name^="properties[Color_"]:checked'
    );
    const selectedColor = Array.from(allColorInputs).find(
      (el) => !/_design_\d\]$/.test(el.getAttribute('name'))
    );
    if (selectedDesign?.value) props['Design'] = capitalize(selectedDesign.value);
    if (selectedColor?.value)  props['Color']  = capitalize(selectedColor.value);
  } else {
    // _design_1 or _design_2: match by suffix on both design and color inputs
    const selectedDesign = wrapper.querySelector(`input[name="properties[design${type}]"]:checked`);
    const selectedColor  = wrapper.querySelector(
      `input[name^="properties[color_"][name$="${type}]"]:checked, ` +
      `input[name^="properties[Color_"][name$="${type}]"]:checked`
    );
    if (selectedDesign?.value) props['Design'] = capitalize(selectedDesign.value);
    if (selectedColor?.value)  props['Color']  = capitalize(selectedColor.value);
  }

  return props;
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Builds Shopify-compatible formData for POST request to /cart/add.js.
 *
 * Every variant gets the global line properties. Variants whose bundle slot
 * ends with _design, _design_1, or _design_2 additionally receive the
 * matching design/color properties. Multiple items of the same type are all handled.
 *
 * @param {string[]} variantIds - List of variant IDs.
 * @param {{ globalProps: Object, designPropsMap: Object }} propsObj
 * @param {Element} wrapper
 * @returns {Object} JSON object structured for Shopify cart API.
 */
function buildCartFormData(variantIds, propsObj, wrapper) {
  const { globalProps, designPropsMap } = propsObj;

  // Map each variant ID to its design type by inspecting data-option wrappers.
  // querySelectorAll handles multiple items of the same type in one pass.
  const variantDesignType = {};
  for (const type of DESIGN_TYPES) {
    wrapper.querySelectorAll(`[data-option$="${type}"]`).forEach((el) => {
      const input = el.querySelector('input');
      if (input?.value) {
        variantDesignType[input.value] = type;
      }
    });
  }

  return {
    items: variantIds.map((id) => {
      const item = {
        id: +id,
        quantity: 1,
        properties: { ...globalProps },
      };

      const designType = variantDesignType[String(id)];
      if (designType && designPropsMap[designType]) {
        Object.assign(item.properties, designPropsMap[designType]);
      }

      return item;
    }),
  };
}

/**
 * Disables the Add to Cart button and updates its text.
 * @param {HTMLButtonElement} button - The button element to disable.
 * @param {string} loadingText - The text to show while loading.
 */
function disableAddToCartBtn(button, loadingText = 'Adding...') {
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  const span = button.querySelector('span');
  if (span) {
    span.dataset.originalText = span.innerText;
    span.innerText = loadingText;
  }
}

/**
 * Enables the Add to Cart button and restores its original text.
 * @param {HTMLButtonElement} button - The button element to enable.
 * @param {string} defaultText - Default fallback text if no original was stored.
 */
function enableAddToCartBtn(button, defaultText = 'Add to Cart') {
  button.disabled = false;
  button.removeAttribute('aria-busy');
  const span = button.querySelector('span');
  if (span) {
    span.innerText = span.dataset.originalText || defaultText;
    delete span.dataset.originalText;
  }
}

/**
 * Shows an add-to-cart error using Dawn's own accessible error region
 * (role="alert", announced automatically) instead of a blocking alert().
 * @param {HTMLFormElement} form - The product form containing the error region.
 * @param {string} message - The error text to display.
 */
function showFormError(form, message) {
  const wrapper = form.querySelector('.product-form__error-message-wrapper');
  if (!wrapper) return;
  const messageEl = wrapper.querySelector('.product-form__error-message');
  if (messageEl) messageEl.textContent = message;
  wrapper.hidden = false;
}

/**
 * Hides a previously shown add-to-cart error, if any.
 * @param {HTMLFormElement} form - The product form containing the error region.
 */
function hideFormError(form) {
  const wrapper = form.querySelector('.product-form__error-message-wrapper');
  if (wrapper) wrapper.hidden = true;
}

/**
 * Sends form data to Shopify's cart API.
 * @param {Object} formData - JSON payload for cart API.
 * @returns {Promise<Object>} Resolves with Shopify's cart response.
 */
function sendToCart(formData) {
  return fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  }).then((response) => {
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  });
}
